import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TBuilderToolbarMenuViewPresentation } from "components/builderShell/BuilderToolbarMenuView";
import { BuilderToolbarMenuPanel } from "components/builderShell/BuilderToolbarMenuPanel";
import { BuilderToolbarMobileSheet } from "components/builderShell/BuilderToolbarMobileSheet";
import { useAppSelector } from "hooks/redux";
import type { TEmailBuilderActivePanel } from "redux/reducers/emailBuilder/emailBuilder";
import { type TSetEmailBuilderActivePanel } from "../hooks/useEmailBuilderPanelSlide";
import { useEmailBuilderViewport } from "../emailBuilderViewportContext";
import { EmailBuilderAddElementsToolbarMenu } from "./addElements/EmailBuilderAddElementsPanel";
import { EmailBuilderCustomizeToolbarMenu } from "./customize/EmailBuilderCustomizeToolbarMenu";
import { EmailBuilderTemplatesToolbarMenu } from "./templates/EmailBuilderTemplatesPanel";
import { EMAIL_BUILDER_Z_LAYERS } from "../emailBuilderShellZLayers";
import { emailBuilderToolbarNavItems } from "../toolbar/emailBuilderToolbarNavItems";
import {
  EMAIL_BUILDER_NAV_SHEET_DEFAULT_SNAP_POINT,
  EMAIL_BUILDER_NAV_SHEET_SNAP_POINTS,
  resolveEmailBuilderPanelSheetSnapPoints,
} from "./emailBuilderPanelSheetSnapPoints";
import "./emailBuilderPanel.scss";

const EMAIL_BUILDER_MENU_PANEL_WIDTH = 350;

interface IEmailBuilderPanelProps {
  panelOpen: boolean;
  closingPanel: boolean;
  closingFromPanel: TEmailBuilderActivePanel | null;
  setActivePanel: TSetEmailBuilderActivePanel;
  mobileToolbarOpen?: boolean;
  mobileChromeReady?: boolean;
  onMobileToolbarClose?: () => void;
  onMobilePanelClose?: () => void;
  onMobileDismissAfterInsert?: () => void;
  onMobileToolbarSelectNavItem?: (panel: Exclude<TEmailBuilderActivePanel, "none">) => void;
}

export const EmailBuilderPanel = forwardRef<HTMLDivElement, IEmailBuilderPanelProps>((props, ref) => {
    const { t } = useTranslation();
    const { usesBottomSheetChrome } = useEmailBuilderViewport();
    const activePanel = useAppSelector(store => store.emailBuilderStore.activePanel);
    const mode = useAppSelector(store => store.emailBuilderStore.mode);
    const [lastActivePanel, setLastActivePanel] = useState<
      Exclude<TEmailBuilderActivePanel, "none">
    >("add-elements");
    const [panelExitLatch, setPanelExitLatch] = useState<{
      displayedPanel: Exclude<TEmailBuilderActivePanel, "none">;
    } | null>(null);
    useEffect(() => {
      if (activePanel !== "none") {
        setLastActivePanel(activePanel);
      }
    }, [activePanel]);

    const mobileNavItems = useMemo(() => emailBuilderToolbarNavItems(t), [t]);

    const closingPanel = props.closingPanel;
    const closingFromPanel = props.closingFromPanel;

    const liveDisplayedPanel: Exclude<TEmailBuilderActivePanel, "none"> =
      activePanel !== "none"
        ? activePanel
        : closingFromPanel !== null && closingFromPanel !== "none"
          ? closingFromPanel
          : lastActivePanel;

    if (closingPanel && panelExitLatch === null) {
      setPanelExitLatch({ displayedPanel: liveDisplayedPanel });
    } else if (!closingPanel && panelExitLatch !== null) {
      setPanelExitLatch(null);
    }

    const displayedPanel = panelExitLatch?.displayedPanel ?? liveDisplayedPanel;

    if (mode === "preview") {
      return null;
    }

    const showContent = activePanel !== "none" || closingPanel;

    const closePanel = () => props.setActivePanel("none");
    const closeAriaLabel = t("Close panel");

    const panelTitle =
      displayedPanel === "colors"
        ? t("Customize")
        : displayedPanel === "templates"
          ? t("Templates")
          : t("Add elements");

    const desktopPresentation: TBuilderToolbarMenuViewPresentation = "desktop-panel";
    const mobilePresentation: TBuilderToolbarMenuViewPresentation = "mobile-sheet-body";

    function renderToolbarMenu(
      presentation: TBuilderToolbarMenuViewPresentation,
      options?: { keepContentWhileSheetClosing?: boolean },
    ) {
      const keepContentWhileSheetClosing = options?.keepContentWhileSheetClosing === true;
      if (!keepContentWhileSheetClosing && !showContent) {
        return null;
      }

      if (displayedPanel === "add-elements") {
        return (
          <EmailBuilderAddElementsToolbarMenu
            presentation={presentation}
            onClose={presentation === "desktop-panel" ? closePanel : undefined}
            closeAriaLabel={presentation === "desktop-panel" ? closeAriaLabel : undefined}
            onAfterInsert={
              presentation === "mobile-sheet-body" ? props.onMobileDismissAfterInsert : undefined
            }
          />
        );
      }

      if (displayedPanel === "colors") {
        return (
          <EmailBuilderCustomizeToolbarMenu
            presentation={presentation}
            onDismissPanel={closePanel}
            onClose={presentation === "desktop-panel" ? closePanel : undefined}
            closeAriaLabel={presentation === "desktop-panel" ? closeAriaLabel : undefined}
          />
        );
      }

      if (displayedPanel === "templates") {
        return (
          <EmailBuilderTemplatesToolbarMenu
            presentation={presentation}
            onDismissPanel={
              presentation === "mobile-sheet-body"
                ? props.onMobileDismissAfterInsert
                : closePanel
            }
            onClose={presentation === "desktop-panel" ? closePanel : undefined}
            closeAriaLabel={presentation === "desktop-panel" ? closeAriaLabel : undefined}
          />
        );
      }

      return null;
    }

    if (usesBottomSheetChrome) {
      const mobileChromeReady = props.mobileChromeReady !== false;
      const mobileToolbarOpen = props.mobileToolbarOpen === true && mobileChromeReady;
      const mobilePanelOpen = activePanel !== "none" && mobileChromeReady;
      const panelSheetSnapPoints = resolveEmailBuilderPanelSheetSnapPoints(displayedPanel);

      return (
        <BuilderToolbarMobileSheet
          ref={ref}
          open={mobileToolbarOpen}
          onOpenChange={nextOpen => {
            if (!nextOpen) {
              props.onMobileToolbarClose?.();
            }
          }}
          panelOpen={mobilePanelOpen}
          panelKey={displayedPanel}
          onPanelOpenChange={nextOpen => {
            if (!nextOpen) {
              props.onMobilePanelClose?.();
            }
          }}
          navTitle={t("Builder tools")}
          panelTitle={panelTitle}
          navItems={mobileNavItems}
          onSelectNavItem={panel => props.onMobileToolbarSelectNavItem?.(panel)}
          navSnapPoints={EMAIL_BUILDER_NAV_SHEET_SNAP_POINTS}
          navDefaultSnapPoint={EMAIL_BUILDER_NAV_SHEET_DEFAULT_SNAP_POINT}
          panelSnapPoints={panelSheetSnapPoints.snapPoints}
          panelDefaultSnapPoint={panelSheetSnapPoints.defaultSnapPoint}
          popupClassName="email-builder-panel-bottom-sheet"
          bodyClassName="email-builder-panel-sheet-body"
          panelPopupClassName="email-builder-panel-bottom-sheet"
          panelBodyClassName="email-builder-panel-sheet-body"
        >
          {renderToolbarMenu(mobilePresentation, { keepContentWhileSheetClosing: true })}
        </BuilderToolbarMobileSheet>
      );
    }

    const panelChromeClassName =
      displayedPanel === "add-elements"
        ? "email-builder-panel builder-toolbar-menu-panel--scroll"
        : "email-builder-panel email-builder-panel--fill";

    return (
      <BuilderToolbarMenuPanel
        ref={ref}
        open={props.panelOpen}
        panelWidth={EMAIL_BUILDER_MENU_PANEL_WIDTH}
        slideTranslateExtra={10}
        className={panelChromeClassName}
        style={{ zIndex: EMAIL_BUILDER_Z_LAYERS.shell.underlay }}
        inertWhenClosed
        role="complementary"
        aria-label={panelTitle}
        aria-hidden={!props.panelOpen}
      >
        {renderToolbarMenu(desktopPresentation, { keepContentWhileSheetClosing: closingPanel })}
      </BuilderToolbarMenuPanel>
    );
});

EmailBuilderPanel.displayName = "EmailBuilderPanel";
